import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle, labelStyle } from '../components/adminTheme';
import stripExif from '../utils/stripExif';
import { uploadToB2 } from '../utils/uploadToB2';

function EditPost() {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [author, setAuthor] = useState('');
    const [coverFile, setCoverFile] = useState(null);
    const [coverUrl, setCoverUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [coAuthors, setCoAuthors] = useState([]);
    const [orgUsers, setOrgUsers] = useState([]);
    const [authorUsername, setAuthorUsername] = useState('');

    useEffect(() => {
        const checkUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/login'); return; }
            const { data: userData } = await supabase
                .from('users')
                .select('username, role')
                .eq('email', data.session?.user?.email)
                .single();
            if (!userData) return;

            setUsername(userData.username);
            setRole(userData.role);

            if (userData.role === 'ORGANISATION' || userData.role === 'ADMIN') {
                const { data: orgs } = await supabase
                    .from('users')
                    .select('username, first_name, last_name')
                    .eq('role', 'ORGANISATION')
                    .neq('username', userData.username);
                if (orgs) setOrgUsers(orgs);
            }

            const { data: postData } = await supabase
                .from('posts')
                .select('author_username')
                .eq('slug', slug)
                .single();

            if (!postData) { navigate('/admin/posts'); return; }
            if (userData.role !== 'ADMIN' && postData.author_username !== userData.username) {
                navigate('/admin/posts');
                return;
            }
        };
        checkUser();
    }, [navigate, slug]);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    useEffect(() => {
        const fetchPost = async () => {
            const { data } = await supabase
                .from('posts')
                .select('*')
                .eq('slug', slug)
                .single();
            if (data) {
                setTitle(data.title);
                setContent(data.content);
                setAuthor(data.author);
                setCoverUrl(data.cover_image || '');
                setLinkUrl(data.link_url || '');
                setCoAuthors(data.co_authors || []);
                setAuthorUsername(data.author_username || '');
            }
        };
        fetchPost();
    }, [slug]);

    const toggleCoAuthor = (uname) => {
        setCoAuthors(prev =>
            prev.includes(uname) ? prev.filter(c => c !== uname) : [...prev, uname]
        );
    };

    const handleSave = async () => {
        setSaving(true);

        let newCoverUrl = coverUrl;
        try {
            if (coverFile) {
                const cleanFile = await stripExif(coverFile);
                newCoverUrl = await uploadToB2(`covers/blog/${slug}/${cleanFile.name}`, cleanFile);
            }

            await supabase.from('posts').update({
                title,
                content,
                author,
                co_authors: coAuthors,
                cover_image: newCoverUrl,
                link_url: linkUrl,
            }).eq('slug', slug);

            setSaving(false);
            alert('Post updated!');
            navigate('/admin/posts');
        } catch (err) {
            console.error('Erreur sauvegarde post:', err);
            setSaving(false);
            alert('Erreur lors de la sauvegarde.');
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .cpost-form-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    max-width: 680px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cpost-form-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .cpost-dropzone {
                    border: 1.5px dashed ${theme.border};
                    padding: 20px;
                    text-align: center;
                    cursor: pointer;
                    transition: border-color .2s ease;
                }
                .cpost-dropzone:hover { border-color: ${theme.borderHover}; }
            `}</style>

            <AdminNav username={username} role={role} onLogout={handleLogout} activePath="/admin/posts" />

            <div className="cpost-form-content">
                <p className="wlink" style={{ ...labelStyle, cursor: 'pointer', display: 'inline-block' }} onClick={() => navigate('/admin/posts')}>← Back to posts</p>
                <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.01em', color: theme.text, margin: '0 0 28px' }}>
                    Edit post
                </h1>

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Title</p>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} style={{ ...inputStyle, fontSize: '16px' }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Author</p>
                    <p style={{ fontFamily: font, fontSize: '14px', fontWeight: 500, color: theme.text, padding: '10px 0', margin: 0 }}>
                        {author} <span style={{ color: theme.textMuted, fontSize: '12px' }}>@{authorUsername}</span>
                    </p>
                </div>

                {(role === 'ORGANISATION' || role === 'ADMIN') && orgUsers.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <p style={labelStyle}>Co-authors (organisations)</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                            {orgUsers.map(u => {
                                const selected = coAuthors.includes(u.username);
                                const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                                return (
                                    <button
                                        key={u.username}
                                        type="button"
                                        onClick={() => toggleCoAuthor(u.username)}
                                        className="wlink"
                                        style={{
                                            background: 'none', border: 'none', fontSize: '12px', cursor: 'pointer', fontFamily: font,
                                            fontWeight: selected ? 700 : 500,
                                            color: selected ? theme.text : theme.textMuted,
                                        }}
                                    >
                                        {label} <span style={{ opacity: 0.6 }}>@{u.username}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <p style={{ fontFamily: font, fontSize: '10px', fontWeight: 500, color: theme.textMuted, margin: '8px 0 0' }}>
                            Le post sera affiché comme une collaboration officielle entre les organisations sélectionnées.
                        </p>
                    </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Cover image</p>
                    {coverUrl && (
                        <img src={coverUrl} alt="cover" style={{ width: '100%', height: '200px', objectFit: 'cover', marginBottom: '10px' }} />
                    )}
                    <div className="cpost-dropzone" onClick={() => document.getElementById('editCoverInput').click()}>
                        <p style={{ fontFamily: font, color: theme.textMuted, fontSize: '14px', fontWeight: 500, margin: 0 }}>{coverFile ? coverFile.name : 'Click to change cover image'}</p>
                        <input id="editCoverInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setCoverFile(e.target.files[0])} />
                    </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <p style={labelStyle}>Content</p>
                    <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={16} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.7' }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Link (optionnel)</p>
                    <input type="text" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://... ou /region/canada/mon-album" style={inputStyle} />
                    <p style={{ fontFamily: font, fontSize: '10.5px', fontWeight: 500, color: theme.textMuted, margin: '6px 0 0' }}>Lien vers une page winston.ie ou un site externe.</p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', alignItems: 'center' }}>
                    <button className="wlink" style={outlineButton} onClick={() => navigate('/admin/posts')}>Cancel</button>
                    <button className="wlink" style={pillButton} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                </div>
            </div>
        </div>
    );
}

export default EditPost;